import React, { useState } from "react";
import { ChevronsUpDown, Star } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandSeparator } from "@/components/ui/command";
import { Checkbox } from "@/components/ui/checkbox";
import { cn } from "@/lib/utils";
import { useFavoriteClients } from "@/hooks/use-favorite-clients";

interface Option {
  value: string;
  label: string;
}

interface MultiSelectWithFavoritesProps {
  options?: Option[];
  placeholder?: string;
  emptyMessage?: string;
  maxDisplay?: number;
  value?: string[];
  onValueChange?: (value: string[]) => void;
  disabled?: boolean;
}

export const MultiSelectWithFavorites: React.FC<MultiSelectWithFavoritesProps> = ({
  options = [],
  placeholder = "Select items...",
  emptyMessage = "No items found.",
  maxDisplay = 3,
  value,
  onValueChange,
  disabled = false,
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const [internalValue, setInternalValue] = useState<string[]>([]);
  const [searchValue, setSearchValue] = useState<string>("");
  const { favorites, toggleFavorite, isFavorite } = useFavoriteClients();

  // Use controlled or uncontrolled state
  const isControlled = value !== undefined;
  const selectedValues = isControlled ? value : internalValue;
  const setSelectedValues = isControlled ? onValueChange! : setInternalValue;

  const handleSelect = (optionValue: string | number) => {
    // Always convert to string to match form validation expectations
    const stringValue = String(optionValue);
    const newValues = selectedValues.includes(stringValue)
      ? selectedValues.filter((item) => item !== stringValue)
      : [...selectedValues, stringValue];

    setSelectedValues(newValues);
  };

  const handleRemove = (optionValue: string | number) => {
    const stringValue = String(optionValue);
    const newValues = selectedValues.filter((item) => item !== stringValue);
    setSelectedValues(newValues);
  };

  const clearAll = () => {
    setSelectedValues([]);
  };

  const handleToggleFavorite = (optionValue: string, e: React.MouseEvent) => {
    e.stopPropagation();
    toggleFavorite(optionValue);
  };

  const selectedItems = options.filter((option) => selectedValues.includes(String(option.value)));
  const displayItems = selectedItems.slice(0, maxDisplay);
  const remainingCount = selectedItems.length - maxDisplay;

  // Group options by favorites and all
  const favoriteOptions = options.filter(opt => favorites.includes(opt.value));
  const nonFavoriteOptions = options.filter(opt => !favorites.includes(opt.value));

  // Filter based on search
  const filteredFavorites = searchValue
    ? favoriteOptions.filter(opt => opt.label.toLowerCase().includes(searchValue.toLowerCase()))
    : favoriteOptions;

  const filteredNonFavorites = searchValue
    ? nonFavoriteOptions.filter(opt => opt.label.toLowerCase().includes(searchValue.toLowerCase()))
    : nonFavoriteOptions;

  const hasResults = filteredFavorites.length > 0 || filteredNonFavorites.length > 0;

  return (
    <div className="w-full">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div
            role="button"
            aria-disabled={disabled}
            className={cn(
              "border-input w-full flex items-center justify-between min-h-10 h-auto py-2 px-3 text-sm",
              "border rounded-md bg-background cursor-pointer",
              "hover:bg-accent hover:text-accent-foreground",
              "focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2",
              disabled && "cursor-not-allowed opacity-50 pointer-events-none",
            )}
            onClick={() => !disabled && setOpen(!open)}
          >
            <div className="flex flex-wrap items-center gap-1 flex-1 min-w-0 pr-2">
              {selectedValues.length === 0 ? (
                <span className="text-muted-foreground">{placeholder}</span>
              ) : (
                <>
                  {displayItems.map((item) => (
                    <div key={item.value} onClick={(e) => e.stopPropagation()}>
                      <Badge variant="secondary" onClose={() => handleRemove(item.value)}>
                        {item.label}
                      </Badge>
                    </div>
                  ))}
                  {remainingCount > 0 && <Badge variant="outline">+{remainingCount} more</Badge>}
                </>
              )}
            </div>
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          </div>
        </PopoverTrigger>
        <PopoverContent 
          className="w-full p-0" 
          align="start"
        >
          <Command shouldFilter={false} className="overflow-hidden">
            <CommandInput placeholder="Search clients..." value={searchValue} onValueChange={setSearchValue} />
            <CommandList className="max-h-[300px] overflow-y-auto overscroll-contain">
              {!hasResults && <CommandEmpty>{emptyMessage}</CommandEmpty>}
              
              {hasResults && (
                <>
                  {selectedValues.length > 0 && (
                    <>
                      <CommandGroup>
                        <CommandItem onSelect={clearAll} className="justify-center text-center">
                          Clear all ({selectedValues.length})
                        </CommandItem>
                      </CommandGroup>
                      <CommandSeparator />
                    </>
                  )}

                  {filteredFavorites.length > 0 && (
                    <>
                      <CommandGroup heading="⭐ Favorites">
                        {filteredFavorites.map((option) => {
                          const isSelected = selectedValues.includes(String(option.value));
                          return (
                            <CommandItem 
                              key={String(option.value)} 
                              value={option.label} 
                              onSelect={() => handleSelect(option.value)}
                              className="flex items-center justify-between"
                            >
                              <div className="flex items-center flex-1">
                                <Checkbox checked={isSelected} className="mr-2" />
                                <span>{option.label}</span>
                              </div>
                              <Star
                                className={cn(
                                  "h-4 w-4 cursor-pointer transition-colors",
                                  "fill-warning-400 text-warning-400"
                                )}
                                onClick={(e) => handleToggleFavorite(option.value, e)}
                              />
                            </CommandItem>
                          );
                        })}
                      </CommandGroup>
                      {filteredNonFavorites.length > 0 && <CommandSeparator />}
                    </>
                  )}

                  {filteredNonFavorites.length > 0 && (
                    <CommandGroup heading={filteredFavorites.length > 0 ? "All Clients" : undefined}>
                      {filteredNonFavorites.map((option) => {
                        const isSelected = selectedValues.includes(String(option.value));
                        return (
                          <CommandItem 
                            key={String(option.value)} 
                            value={option.label} 
                            onSelect={() => handleSelect(option.value)}
                            className="flex items-center justify-between"
                          >
                            <div className="flex items-center flex-1">
                              <Checkbox checked={isSelected} className="mr-2" />
                              <span>{option.label}</span>
                            </div>
                            <Star
                              className={cn(
                                "h-4 w-4 cursor-pointer transition-colors",
                                "text-muted-foreground hover:text-warning-400"
                              )}
                              onClick={(e) => handleToggleFavorite(option.value, e)}
                            />
                          </CommandItem>
                        );
                      })}
                    </CommandGroup>
                  )}
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};
